
import { useState, useEffect, useCallback } from 'react';
import { realTimeSystemMetricsService, RealTimeSystemMetrics } from '@/services/realTimeSystemMetricsService';
import { toast } from '@/components/ui/sonner';
import { logger } from '@/utils/logger';

export const useRealTimeSystemMetrics = () => {
  const [metrics, setMetrics] = useState<RealTimeSystemMetrics>({
    serverStatus: 'healthy',
    databaseStatus: 'healthy',
    uptime: '99.9%',
    activeUsers: 0,
    serverLoad: 45,
    memoryUsage: 68,
    storageUsed: 42,
    responseTime: 85,
    errorRate: 0.02,
    lastBackup: '2 hours ago',
    securityStatus: 'secured'
  });

  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  const loadMetrics = useCallback(async () => {
    try {
      setLoading(true);
      const latestMetrics = await realTimeSystemMetricsService.getLatestMetrics();
      setMetrics(latestMetrics);
      setLastUpdate(new Date());
      setIsConnected(true);
      logger.debug('System metrics loaded', latestMetrics);
    } catch (error) {
      logger.error('Failed to load system metrics:', error);
      toast.error('Failed to load system metrics');
      setIsConnected(false);
    } finally {
      setLoading(false);
    }
  }, []);

    const simulateUpdate = useCallback(async () => {
      try {
        await realTimeSystemMetricsService.recordMetricUpdate();
        toast.success('System metrics updated');
      } catch (error) {
        logger.error('Failed to record metric update:', error);
        toast.error('Failed to update metrics');
      }
    }, []);

  const handleMetricsUpdate = useCallback((updatedMetrics: RealTimeSystemMetrics) => {
    setMetrics(updatedMetrics);
    setLastUpdate(new Date());
    logger.debug('Real-time metrics updated', updatedMetrics);
  }, []);

  useEffect(() => {
    loadMetrics();

    // Set up real-time subscription
    const channel = realTimeSystemMetricsService.subscribeToMetrics(handleMetricsUpdate);

    // Set up periodic updates every 30 seconds
    const interval = setInterval(() => {
      realTimeSystemMetricsService.recordMetricUpdate().catch(error => {
        logger.error('Failed to record periodic metric update:', error);
      });
    }, 30000);

    // Initial metric recording
    setTimeout(() => {
      realTimeSystemMetricsService.recordMetricUpdate().catch(error => {
        logger.error('Failed to record initial metric update:', error);
      });
    }, 2000);

    return () => {
      realTimeSystemMetricsService.unsubscribe(channel);
      clearInterval(interval);
    };
  }, [loadMetrics, handleMetricsUpdate]);

  return {
    metrics,
    loading,
    isConnected,
    lastUpdate,
    refreshMetrics: loadMetrics,
    simulateUpdate
  };
};
